import React, { useState, useMemo } from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { media } from '@atlaskit/primitives/responsive';
import { Heading, Link, LinkStyle, DropdownMenuCheckbox } from '@atlassian/learning-components';
import { useLoaderData, useNavigate } from 'react-router-dom';

import { LiveTrainingCard, LiveTrainingCardProps } from 'src/components/LiveTrainingCard';
import { FlattenedLearningInstance } from 'src/utils/almDataTransformer';
import { MyLearningLoaderProps } from 'src/pages/MyLearningPage/loader';

type StatusFilter = 'all' | 'upcoming' | 'completed';
type SortOrder = 'newest' | 'oldest';

interface FilterItem {
  isSelected: boolean;
  isDisabled: boolean;
}

const statusFilterMapping: Record<string, StatusFilter> = {
  All: 'all',
  Upcoming: 'upcoming',
  Completed: 'completed',
};

const sortOrderMapping: Record<string, SortOrder> = {
  'Newest first': 'newest',
  'Oldest first': 'oldest',
};

export const LiveTrainingTab = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortOrder, setSortOrder] = useState<SortOrder>('newest');
  const { liveTrainingData } = useLoaderData() as MyLearningLoaderProps;

  const liveTrainingSessions: LiveTrainingCardProps[] = useMemo(
    () =>
      liveTrainingData?.map((session: FlattenedLearningInstance) => ({
        id: session.instance.id,
        title: session.title,
        subtitle: session.description,
        dateTime: session.instance.dateTime || '',
        status: session.instance.status,
        enrolled: session.instance.enrolled,
        product: session.instance.product,
        label: session.instance.label,
        host: session.instance.host,
        detailsLink: session.instance.detailsLink,
        showAddToCalendar: session.instance.status === 'upcoming' && !session.instance.enrolled,
        showDownloadMaterials: session.instance.enrolled,
      })) || [],
    [liveTrainingData],
  );

  const handleDetailsClick = (detailsLink: string): void => {
    // eslint-disable-next-line no-console
    console.log('Navigate to:', detailsLink);
    // TODO: Implement navigation when route is ready
  };

  const handleAddToCalendar = (title: string): void => {
    // eslint-disable-next-line no-console
    console.log('Add to calendar:', title);
    // TODO: Implement add to calendar functionality
  };

  const handleDownloadMaterials = (title: string): void => {
    // eslint-disable-next-line no-console
    console.log('Download materials for:', title);
    // TODO: Implement download materials functionality
  };

  const handleBrowsePublicClasses = (): void => {
    navigate('/calendar');
  };

  const handleStatusFilterChange = (label: string): void => {
    const newFilter = statusFilterMapping[label];

    if (newFilter) {
      setStatusFilter(newFilter);
    }
  };

  const handleSortOrderChange = (label: string): void => {
    const newSort = sortOrderMapping[label];

    if (newSort) {
      setSortOrder(newSort);
    }
  };

  const statusFilterItems: Record<string, FilterItem> = {
    All: { isSelected: statusFilter === statusFilterMapping.All, isDisabled: false },
    Upcoming: { isSelected: statusFilter === statusFilterMapping.Upcoming, isDisabled: false },
    Completed: { isSelected: statusFilter === statusFilterMapping.Completed, isDisabled: false },
  };

  const sortOrderItems: Record<string, FilterItem> = {
    'Newest first': { isSelected: sortOrder === sortOrderMapping['Newest first'], isDisabled: false },
    'Oldest first': { isSelected: sortOrder === sortOrderMapping['Oldest first'], isDisabled: false },
  };

  const filteredAndSortedSessions = useMemo(() => {
    // Filter by status
    const filtered =
      statusFilter === 'all'
        ? liveTrainingSessions
        : liveTrainingSessions.filter((session) => session.status === statusFilter);

    // Sort by date
    const sorted = [...filtered].sort((a, b) => {
      const dateA = new Date(a.dateTime).getTime();
      const dateB = new Date(b.dateTime).getTime();

      return sortOrder === sortOrderMapping['Newest first'] ? dateB - dateA : dateA - dateB;
    });

    return sorted;
  }, [statusFilter, sortOrder, liveTrainingSessions]);

  return (
    <div css={containerStyles} data-testid="live-training-tab">
      <div css={headerContainerStyles}>
        <div css={browseLinkContainerStyles}>
          <Link
            link="/calendar"
            label="Browse public classes"
            style={LinkStyle.SEMIBOLD}
            icon="arrow-right"
            onClick={handleBrowsePublicClasses}
            data-testid="browse-public-classes-link"
          />
        </div>

        <div css={filtersContainerStyles}>
          <div css={filterGroupStyles}>
            <span css={filterLabelStyles}>Filter by status:</span>
            <DropdownMenuCheckbox
              data-testid="status-filter-dropdown"
              items={statusFilterItems}
              onItemClick={handleStatusFilterChange}
              placement="bottom-end"
            />
          </div>

          <div css={filterGroupStyles}>
            <span css={filterLabelStyles}>Sort by date:</span>
            <DropdownMenuCheckbox
              data-testid="sort-order-dropdown"
              items={sortOrderItems}
              onItemClick={handleSortOrderChange}
              placement="bottom-end"
            />
          </div>
        </div>
      </div>

      {filteredAndSortedSessions.length > 0 ? (
        <div css={cardsGridStyles}>
          {filteredAndSortedSessions.map((training) => (
            <LiveTrainingCard
              key={training.id}
              {...training}
              onDetailsClick={(): void => handleDetailsClick(training.detailsLink)}
              onAddToCalendar={training.showAddToCalendar ? (): void => handleAddToCalendar(training.title) : undefined}
              onDownloadMaterials={
                training.showDownloadMaterials ? (): void => handleDownloadMaterials(training.title) : undefined
              }
            />
          ))}
        </div>
      ) : (
        <div css={emptyStateStyles}>
          <Heading level={3}>No training sessions found</Heading>
          <p>Try adjusting your filters to see more results.</p>
        </div>
      )}
    </div>
  );
};

const containerStyles = css({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.400'),
  paddingTop: token('space.300'),
});

const headerContainerStyles = css({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.300'),
  marginBottom: token('space.200'),
});

const browseLinkContainerStyles = css({
  display: 'flex',
  alignItems: 'center',

  a: {
    fontSize: '16px',
    fontWeight: 600,
  },
});

const filtersContainerStyles = css({
  display: 'flex',
  gap: token('space.300'),
  alignItems: 'center',
  flexWrap: 'wrap',

  [media.below.sm]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
  },
});

const filterGroupStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: token('space.100'),

  [media.below.sm]: {
    width: '100%',
  },
});

const filterLabelStyles = css({
  fontSize: '14px',
  color: token('color.text.subtle'),
  fontWeight: 500,
});

const cardsGridStyles = css({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
  gap: token('space.300'),
  width: '100%',

  [media.below.md]: {
    gridTemplateColumns: '1fr',
  },
});

const emptyStateStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: token('space.600'),
  textAlign: 'center',
  gap: token('space.200'),

  p: {
    color: token('color.text.subtle'),
    margin: 0,
  },
});
